import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';

/**
 * One member suggested to another for today (P16b).
 *
 * **An id, a score and a place in the list — and nothing else.** Exactly the
 * discipline `LikeSummarySchema` keeps, for the same reason: a client draws the
 * card by asking the visibility resolver what this viewer may see of this member
 * (N18), so no member-to-member answer data ever travels through the matching
 * module. The scorer reads `profile_answers` to *rank* candidates and no answer
 * **value** leaves it — what comes out is arithmetic.
 *
 * **`score` is here and the breakdown is not.** The number is the ordering the
 * member is being shown and is safe to hand them; the breakdown names the
 * questions it scored, which is an operator's view of the matching policy and
 * not a member's. It stays in the row (`NominationScoreBreakdownSchema`).
 *
 * **`rank` is the server's order, not the array index.** A client that sorted
 * the array itself would re-derive an order the server already decided under the
 * operator's weights — and would get it wrong the moment two candidates tie,
 * because the tiebreak is part of what makes today's batch reproducible.
 */
export const NominationSchema = z
  .object({
    /** The suggested member. The only fact about them that crosses this boundary. */
    userId: UuidSchema,
    /**
     * The weighted total under the weights in force when the batch was computed.
     *
     * Not an integer: the completion and overlap terms are fractions of their
     * weights, so a whole number here would be a rounding nobody asked for.
     */
    score: z.number().min(0),
    /** 1-based position within the day's batch. */
    rank: z.number().int().min(1),
  })
  .strict();

export type Nomination = z.infer<typeof NominationSchema>;
